/**
 * MobileProducts — Products / Food Items browser (mobile).
 *
 * Layout: 4 category tiles → location → search + Country/Category/Sub-Cat
 * dropdowns → alphabetical product cards with "Show more" pagination.
 * Reads from the same /api/food-items catalogue as the desktop directory,
 * so all 887 items show by default; dropdowns narrow the view.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronRight, Search, ChevronDown, Package } from "lucide-react";
import MobileLocationBar from "./MobileLocationBar";
import { CATEGORY_TILES, CategoryTile } from "./MobileHome";

const API = process.env.REACT_APP_BACKEND_URL;

// Categories must match backend food_items.category values.
const CATEGORY_OPTS = [
  { value: "leafy-greens",  label: "Leafy Greens" },
  { value: "fruits",        label: "Fruits" },
  { value: "vegetables",    label: "Vegetables" },
  { value: "herbs",         label: "Herbs" },
  { value: "spices",        label: "Spices" },
  { value: "grains",        label: "Grains" },
  { value: "legumes",       label: "Legumes" },
  { value: "nuts-seeds",    label: "Nuts & Seeds" },
  { value: "dairy",         label: "Dairy" },
  { value: "eggs",          label: "Eggs" },
  { value: "meat",          label: "Meat & Poultry" },
  { value: "fish",          label: "Fish & Seafood" },
  { value: "beverages",     label: "Beverages" },
  { value: "non-food",      label: "Non-food" },
];

const PERISHABLE = new Set([
  "leafy-greens", "fruits", "vegetables", "herbs", "dairy", "eggs", "meat", "fish",
]);

const CATEGORY_TAG_STYLE = {
  "leafy-greens": { bg: "#82C049", color: "#0d2b0d" },
  fruits:         { bg: "#82C049", color: "#0d2b0d" },
  vegetables:     { bg: "#82C049", color: "#0d2b0d" },
  herbs:          { bg: "#82C049", color: "#0d2b0d" },
  dairy:          { bg: "#a8d5f5", color: "#0d2b40" },
  meat:           { bg: "#c74019", color: "#fff" },
  fish:           { bg: "#4C96D2", color: "#fff" },
  grains:         { bg: "#c39a5c", color: "#3A2A00" },
  Perishable:     { bg: "#F9C846", color: "#3A2A00" },
};

function Tag({ label, styleKey }) {
  const s = CATEGORY_TAG_STYLE[styleKey || label] || { bg: "#eee", color: "#222" };
  return (
    <span className="inline-block text-[11px] font-bold rounded-md px-2 py-0.5" style={{ background: s.bg, color: s.color }}>
      {label}
    </span>
  );
}

function DropSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="relative flex items-center rounded-xl bg-white shadow-sm border border-gray-200" data-testid={testId}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full appearance-none bg-transparent px-3 py-3 text-base outline-none pr-9">
        <option value="">{label}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 w-4 h-4 opacity-60" />
    </label>
  );
}

export default function MobileProducts() {
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ]                   = useState("");
  const [category, setCategory]     = useState("");
  const [sub, setSub]               = useState("");
  const PAGE_SIZE = 40;
  const [visible, setVisible]       = useState(PAGE_SIZE);

  useEffect(() => {
    setLoading(true);
    setVisible(PAGE_SIZE);
    axios.get(`${API}/api/food-items`, {
      params: {
        q: q || undefined,
        category: category || undefined,
        sub_category: sub || undefined,
        limit: 1000,   // pull the whole catalogue up-front — client-side paginated below
      },
    })
    .then((r) => {
      const its = (r.data.items || []).sort((a, b) =>
        (a.common_name || "").localeCompare(b.common_name || ""));
      setItems(its);
      setTotal(r.data.total || its.length);
    })
    .catch(() => setItems([]))
    .finally(() => setLoading(false));
  }, [q, category, sub]);

  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setVisible((n) => Math.min(n + PAGE_SIZE, items.length));
      setLoadingMore(false);
    }, 120);
  };

  const shown = items.slice(0, visible);

  return (
    <div data-testid="mobile-products">
      {/* Category tiles with Products active */}
      <div className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
           style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}>
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} active={t.key === "products"} />
        ))}
      </div>

      {/* Location */}
      <div className="flex items-center justify-center mt-3 mb-3">
        <MobileLocationBar testid="products-location" />
      </div>

      {/* Filters */}
      <div className="px-4 grid grid-cols-2 gap-3">
        <div className="col-span-1 flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-200 px-3 py-3">
          <input data-testid="products-search-input" type="search"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search Product"
                 className="flex-1 outline-none bg-transparent text-base" />
          <Search className="w-4 h-4 opacity-60" />
        </div>
        <DropSelect label="United States" value="" onChange={() => {}}
                    testId="products-filter-country"
                    options={[{ value: "us", label: "United States" }]} />
        <DropSelect label="Select Category" value={category} onChange={setCategory}
                    testId="products-filter-category"
                    options={CATEGORY_OPTS} />
        <DropSelect label="Select Sub-Category" value={sub} onChange={setSub}
                    testId="products-filter-subcategory"
                    options={[
                      { value: "leafy-greens", label: "Leafy Greens" },
                      { value: "root-vegetables", label: "Root Vegetables" },
                      { value: "citrus", label: "Citrus" },
                      { value: "berries", label: "Berries" },
                      { value: "cheese", label: "Cheese" },
                      { value: "yogurt", label: "Yogurt" },
                    ]} />
      </div>

      {/* Results count */}
      {!loading && items.length > 0 && (
        <div className="px-4 mt-4 text-[11px] font-mono uppercase tracking-widest opacity-70" data-testid="products-count">
          Showing {Math.min(visible, items.length).toLocaleString()} of {total.toLocaleString()} — alphabetical
        </div>
      )}

      {/* Results */}
      <div className="mt-3 px-4 pb-16 space-y-3" data-testid="products-list">
        {loading ? (
          <div className="text-sm opacity-60" data-testid="products-loading">Loading products…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed p-8 text-center bg-white" data-testid="products-empty">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <div className="font-bold mb-1">No products match your filters.</div>
            <button type="button"
                    onClick={() => { setQ(""); setCategory(""); setSub(""); }}
                    className="text-sm underline mt-1"
                    data-testid="products-clear-filters">
              Clear filters
            </button>
          </div>
        ) : (
          <>
            {shown.map((it) => {
              const name = it.common_name || it.slug;
              const cover = it.hero_image
                          || (it.images && it.images[0]?.url)
                          || null;
              const perishable = PERISHABLE.has(it.category);
              const catLabel = (CATEGORY_OPTS.find((c) => c.value === it.category)?.label) || it.category;
              return (
                <Link to={`/food-pulse/food/${it.slug}`}
                      key={it.slug}
                      data-testid={`products-item-${it.slug}`}
                      className="flex items-center gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
                  {cover ? (
                    <img src={cover} alt={name} loading="lazy"
                         className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0"
                         style={{ background: "#EFEBE3" }}>
                      <Package className="w-8 h-8 opacity-30" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold truncate">{name}</div>
                    <div className="mt-1 flex gap-1.5 flex-wrap">
                      {perishable && <Tag label="Perishable" />}
                      {catLabel && <Tag label={catLabel} styleKey={it.category} />}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 opacity-60 flex-shrink-0" />
                </Link>
              );
            })}
            {visible < items.length && (
              <div className="text-center pt-2">
                <button type="button" onClick={loadMore} disabled={loadingMore}
                        className="rounded-full px-5 py-3 text-white font-semibold shadow-sm disabled:opacity-60 transition"
                        style={{ background: "#653091" }}
                        data-testid="products-load-more">
                  {loadingMore ? "Loading…" : `Show ${Math.min(PAGE_SIZE, items.length - visible)} more`}
                </button>
                <div className="text-[11px] opacity-60 mt-2">
                  {(items.length - visible).toLocaleString()} more items
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
